import { OverlayTooltip, type OverlayProps } from 'components/Overlay';
import { useFittingItemCount } from 'hooks/useFittingItemCount';
import { cn } from 'utils/style-utils';

const GAP = 4;
/** Chips shrink down to this width, truncating their text, before collapsing. */
const MIN_CHIP_WIDTH = 60;

const moreBadgeClassName =
  'flex h-6 shrink-0 cursor-pointer items-center rounded-lg bg-gray-200 p-1 text-xs leading-4 font-medium text-gray-900';

interface FittingTagChipsProps {
  options: string[];
  renderChip: (option: string) => React.ReactNode;
  /** Content of the "+N" badge's tooltip, listing the options that don't fit. */
  renderHidden: (options: string[]) => React.ReactNode;
  onMoreClick: () => void;
  overlayProps?: OverlayProps;
}

/**
 * The chips that fit in the row's width, then "+N" for the rest. Chips shrink
 * to 60px before one collapses; with no room for even one, only "+N" shows.
 */
export function FittingTagChips({
  options,
  renderChip,
  renderHidden,
  onMoreClick,
  overlayProps,
}: FittingTagChipsProps) {
  const { containerRef, visibleCount, itemWidths } = useFittingItemCount(
    options.join('\u0000'),
    GAP,
    MIN_CHIP_WIDTH,
  );
  const measuring = visibleCount === null;
  const shown = measuring ? options : options.slice(0, visibleCount);
  const hidden = measuring ? [] : options.slice(visibleCount);

  return (
    <div
      ref={containerRef}
      className="flex w-full min-w-0 items-center gap-1 overflow-hidden"
    >
      {shown.map((option, index) => (
        <div
          key={option}
          data-fit-item
          className={cn(
            'flex max-w-35',
            measuring || (itemWidths?.[index] ?? 0) <= MIN_CHIP_WIDTH
              ? 'shrink-0'
              : 'min-w-15 shrink',
          )}
        >
          {renderChip(option)}
        </div>
      ))}
      {measuring && (
        <div data-fit-more className={moreBadgeClassName}>
          +{options.length}
        </div>
      )}
      {hidden.length > 0 && (
        <OverlayTooltip
          content={renderHidden(hidden)}
          tone="light"
          delayDuration={400}
          overlayProps={overlayProps}
        >
          <div className={moreBadgeClassName} onClick={onMoreClick}>
            +{hidden.length}
          </div>
        </OverlayTooltip>
      )}
    </div>
  );
}
